import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Toast,
    ToastTitle,
    ToastBody,
    useId,
    useToastController,
    Toaster,
} from '@fluentui/react-components';
import { useActiveConversationsSignalR } from '../../../hooks/useActiveConversationsSignalR';
import { useAppDispatch, useSelector } from '../../../store/hooks';
import { selectActiveConversations } from '../../../store/features/activeConversations/activeConversationsSlice';
import { useTranslation } from 'react-i18next';

interface ToastNotificationProps {
    teamId: string | null;
}

const ToastNotification: React.FC<ToastNotificationProps> = ({ teamId }) => {
    const { t } = useTranslation();
    const toasterId = useId('notification-toaster');
    const { dispatchToast } = useToastController(toasterId);
    const previousConnectionStateRef = useRef<boolean | null>(null);
    const previousItemCountRef = useRef<number>(0);
    const dispatch = useAppDispatch();

    const notifications = useMemo(
        () => ({
            connected: {
                title: t('Toast_Connected_Title'),
                body: t('Toast_Connected_Body'),
                intent: 'success' as const,
            },
            disconnected: {
                title: t('Toast_Disconnected_Title'),
                body: t('Toast_Disconnected_Body'),
                intent: 'warning' as const,
            },
            resolved: {
                title: t('Toast_Resolved_Title'),
                body: t('Toast_Resolved_Body'),
                intent: 'success' as const,
            },
        }),
        [t]
    );

    const { isConnected } = useActiveConversationsSignalR(teamId, !!teamId);
    const { items } = useSelector(selectActiveConversations);

    const showNotification = useCallback(
        (key: keyof typeof notifications) => {
            const notification = notifications[key];
            dispatchToast(
                <Toast>
                    <ToastTitle>{notification.title}</ToastTitle>
                    <ToastBody>{notification.body}</ToastBody>
                </Toast>,
                { intent: notification.intent }
            );
        },
        [dispatchToast, notifications]
    );

    useEffect(() => {
        // Skip initial render
        if (previousConnectionStateRef.current === null) {
            previousConnectionStateRef.current = isConnected;
            previousItemCountRef.current = items.length;
            return;
        }

        // Handle connection state changes
        if (previousConnectionStateRef.current !== isConnected) {
            showNotification(isConnected ? 'connected' : 'disconnected');
            previousConnectionStateRef.current = isConnected;
        }

        // Handle conversation resolved (when item count decreases and SignalR is connected)
        if (previousItemCountRef.current > items.length && isConnected) {
            showNotification('resolved');
        }
        previousItemCountRef.current = items.length;
    }, [isConnected, items.length, showNotification, dispatch]);

    return <Toaster toasterId={toasterId} />;
};

export default ToastNotification;
